/**
 * Geometria dell'editor della foto (research R-03), in funzioni pure e senza DOM.
 *
 * Unità: lo spostamento si misura in frazioni dell'altezza del riquadro di ritaglio (1 = la sua
 * altezza; nel riquadro quadrato coincide con il lato), così non dipende dalla misura con cui il
 * riquadro compare sullo schermo. Il riquadro ha il rapporto `aspect` = larghezza / altezza (1 per
 * la foto del profilo, 16/9 per l'immagine del cantiere). Lo zoom 1 è la "copertura": la foto (già
 * ruotata) riempie esattamente il riquadro sul lato che lo richiede.
 *
 * Sistema di riferimento: x verso destra, y verso il basso, come sullo schermo e sul canvas; una
 * rotazione positiva gira in senso orario.
 */

export type CropRotation = 0 | 90 | 180 | 270;

export interface CropState {
  /** Ingrandimento rispetto alla copertura, da `MIN_ZOOM` a `MAX_ZOOM`. */
  readonly zoom: number;
  /** Spostamento del centro della foto dal centro del riquadro, in lati del riquadro. */
  readonly offsetX: number;
  readonly offsetY: number;
  /** Rotazione in gradi, a passi di 90 in senso orario. */
  readonly rotation: CropRotation;
}

export interface Size {
  readonly width: number;
  readonly height: number;
}

/** Rettangolo da ritagliare, in pixel della foto già ruotata. */
export interface CropRect {
  readonly x: number;
  readonly y: number;
  /** Altezza del rettangolo: nel quadrato è il lato, nel 16:9 il lato corto. */
  readonly size: number;
  readonly width: number;
  readonly height: number;
}

/** Trasformazione da applicare al canvas prima di `drawImage(foto, -w/2, -h/2)`. */
export interface DrawTransform {
  readonly translateX: number;
  readonly translateY: number;
  /** In radianti. */
  readonly rotation: number;
  readonly scale: number;
}

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;

/** Stato iniziale: foto centrata che copre il riquadro, senza rotazione ("Ripristina"). */
export const INITIAL_CROP: CropState = { zoom: 1, offsetX: 0, offsetY: 0, rotation: 0 };

/** Misure della foto dopo la rotazione. */
export function rotatedSize(source: Size, rotation: CropRotation): Size {
  return rotation === 90 || rotation === 270
    ? { width: source.height, height: source.width }
    : { width: source.width, height: source.height };
}

/** Altezze del riquadro per pixel della foto ruotata, a zoom 1 (copertura). */
function unitScale(source: Size, rotation: CropRotation, aspect: number): number {
  const rotated = rotatedSize(source, rotation);
  return Math.max(aspect / rotated.width, 1 / rotated.height);
}

/** Misure della foto mostrata, in altezze del riquadro (almeno quanto il riquadro). */
export function displaySize(source: Size, state: CropState, aspect = 1): Size {
  const rotated = rotatedSize(source, state.rotation);
  const scale = unitScale(source, state.rotation, aspect) * state.zoom;
  return { width: rotated.width * scale, height: rotated.height * scale };
}

/** Scala di copertura: pixel del riquadro per pixel della foto, a zoom 1. */
export function coverScale(source: Size, viewportSize: number): number {
  return viewportSize / Math.min(source.width, source.height);
}

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) {
    return MIN_ZOOM;
  }
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

/** Riporta zoom e spostamento nei limiti: la foto copre sempre tutto il riquadro. */
export function clampCrop(source: Size, state: CropState, aspect = 1): CropState {
  const zoom = clampZoom(state.zoom);
  const display = displaySize(source, { ...state, zoom }, aspect);
  const maxX = Math.max(0, (display.width - aspect) / 2);
  const maxY = Math.max(0, (display.height - 1) / 2);
  return {
    zoom,
    offsetX: clampValue(state.offsetX, maxX),
    offsetY: clampValue(state.offsetY, maxY),
    rotation: state.rotation,
  };
}

/** Sposta la foto di `dx`, `dy` lati del riquadro (positivi verso destra e in basso). */
export function panCrop(
  source: Size,
  state: CropState,
  dx: number,
  dy: number,
  aspect = 1,
): CropState {
  return clampCrop(
    source,
    { ...state, offsetX: state.offsetX + dx, offsetY: state.offsetY + dy },
    aspect,
  );
}

/**
 * Cambia lo zoom tenendo fermo il punto sotto `anchorX`, `anchorY` (in lati del riquadro dal suo
 * centro): la rotella e le due dita ingrandiscono dove sono, il cursore e i tasti al centro.
 */
export function zoomCrop(
  source: Size,
  state: CropState,
  zoom: number,
  anchorX = 0,
  anchorY = 0,
  aspect = 1,
): CropState {
  const next = clampZoom(zoom);
  const ratio = next / state.zoom;
  return clampCrop(
    source,
    {
      ...state,
      zoom: next,
      offsetX: anchorX - (anchorX - state.offsetX) * ratio,
      offsetY: anchorY - (anchorY - state.offsetY) * ratio,
    },
    aspect,
  );
}

/** Ruota di 90° la foto attorno al centro del riquadro: il punto inquadrato resta inquadrato. */
export function rotateCrop(
  source: Size,
  state: CropState,
  direction: 'left' | 'right',
  aspect = 1,
): CropState {
  const rotation = ((state.rotation + (direction === 'right' ? 90 : 270)) % 360) as CropRotation;
  // In senso orario (x, y) → (−y, x); in senso antiorario (x, y) → (y, −x).
  const offsetX = direction === 'right' ? -state.offsetY : state.offsetY;
  const offsetY = direction === 'right' ? state.offsetX : -state.offsetX;
  return clampCrop(source, { ...state, rotation, offsetX, offsetY }, aspect);
}

/** Rettangolo inquadrato, in pixel della foto già ruotata. */
export function cropRect(source: Size, state: CropState, aspect = 1): CropRect {
  const rotated = rotatedSize(source, state.rotation);
  const height = 1 / (unitScale(source, state.rotation, aspect) * state.zoom);
  const width = height * aspect;
  return {
    x: rotated.width / 2 - state.offsetX * height - width / 2,
    y: rotated.height / 2 - state.offsetY * height - height / 2,
    size: height,
    width,
    height,
  };
}

/**
 * Trasformazione per disegnare l'inquadratura su un rettangolo alto `outputHeight` pixel e largo
 * `outputHeight × aspect`: la usano il riquadro sullo schermo, le anteprime e il file esportato,
 * che quindi coincidono.
 */
export function drawTransform(
  source: Size,
  state: CropState,
  outputHeight: number,
  aspect = 1,
): DrawTransform {
  return {
    translateX: (outputHeight * aspect) / 2 + state.offsetX * outputHeight,
    translateY: outputHeight / 2 + state.offsetY * outputHeight,
    rotation: (state.rotation * Math.PI) / 180,
    scale: outputHeight * unitScale(source, state.rotation, aspect) * state.zoom,
  };
}

/** Applica la trasformazione a un punto della foto originale (coordinate di `drawImage`). */
export function projectPoint(
  source: Size,
  transform: DrawTransform,
  x: number,
  y: number,
): { x: number; y: number } {
  const dx = (x - source.width / 2) * transform.scale;
  const dy = (y - source.height / 2) * transform.scale;
  const cos = Math.cos(transform.rotation);
  const sin = Math.sin(transform.rotation);
  return {
    x: transform.translateX + dx * cos - dy * sin,
    y: transform.translateY + dx * sin + dy * cos,
  };
}

/** Uguaglianza a meno degli errori di arrotondamento, per evitare eventi `changed` inutili. */
export function sameCrop(a: CropState, b: CropState): boolean {
  const near = (p: number, q: number) => Math.abs(p - q) < 1e-9;
  return (
    a.rotation === b.rotation &&
    near(a.zoom, b.zoom) &&
    near(a.offsetX, b.offsetX) &&
    near(a.offsetY, b.offsetY)
  );
}

function clampValue(value: number, limit: number): number {
  // `+ 0` toglie lo zero negativo, che confonderebbe i confronti nei test.
  return Math.min(limit, Math.max(-limit, value)) + 0;
}
